/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  DateFilterFn,
  MatDatepickerInputEvent,
} from '@angular/material/datepicker';
import {
  FloatLabelType,
  MatFormFieldAppearance,
} from '@angular/material/form-field';
import {PtBrDateAdapter} from './utils/custom-ptbr-date-adapter';
import {matDateFormatsPtBr} from './utils/date-formats';

/** Control value: single date or range, always ISO 8601 (YYYY-MM-DD) strings. */
export type GateweeDateValue =
  | string
  | {start: string | null; end: string | null}
  | null;

@Component({
  selector: 'gatewee-datepicker',
  templateUrl: './gatewee-datepicker.component.html',
  styleUrls: ['./gatewee-datepicker.component.css'],
  providers: [
    {provide: DateAdapter, useClass: PtBrDateAdapter},
    {provide: MAT_DATE_FORMATS, useValue: matDateFormatsPtBr},
    {provide: MAT_DATE_LOCALE, useValue: 'pt-BR'},
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GateweeDatepickerComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() controlName!: string;
  @Input() label = 'Data';
  @Input() placeholder?: string;
  @Input() mode: 'single' | 'range' = 'single';
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() disabled = false;
  @Input() required = false;
  @Input() matDatepickerFilter: DateFilterFn<Date | null> | null = null;
  @Input() customMessages: Record<string, string> = {};
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  @Input() startAt: Date | null = null;
  @Input() clearable = true;

  floatLabel: FloatLabelType = 'auto';

  constructor(private adapter: DateAdapter<Date>) {}

  ngOnInit(): void {
    this.adapter.setLocale?.('pt-BR');
  }

  get control(): FormControl {
    const ctrl = this.form.get(this.controlName);
    if (!ctrl) throw new Error(`Control "${this.controlName}" não existe.`);
    return ctrl as FormControl;
  }

  // ===== Helpers =====
  /**
   * Formats a Date to ISO 8601 string (YYYY-MM-DD)
   */
  private formatDateToISO(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Converts ISO 8601 string (YYYY-MM-DD) to Date object
   */
  private parseISOToDate(isoString: string | null | undefined): Date | null {
    if (!isoString) return null;
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Gets the Date value for display in Material datepicker
   * Material datepicker always expects Date objects for display
   */
  private getDisplayDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string') return this.parseISOToDate(value);
    return null;
  }

  get placeholderText(): string {
    if (this.placeholder) return this.placeholder;
    return this.mode === 'single' ? 'dd/mm/aaaa' : 'dd/mm/aaaa – dd/mm/aaaa';
  }

  /**
   * Gets the Date value for single mode display
   */
  get displayValue(): Date | null {
    if (this.mode !== 'single') return null;
    return this.getDisplayDate(this.control.value);
  }

  /**
   * Gets the start Date for range mode display
   */
  get start(): Date | null {
    if (this.mode !== 'range') return null;
    const value = this.control.value;
    if (!value || typeof value !== 'object') return null;
    return this.getDisplayDate(value.start);
  }

  /**
   * Gets the end Date for range mode display
   */
  get end(): Date | null {
    if (this.mode !== 'range') return null;
    const value = this.control.value;
    if (!value || typeof value !== 'object') return null;
    return this.getDisplayDate(value.end);
  }

  // ===== Handlers =====
  onSingleInputChange(event: MatDatepickerInputEvent<Date>): void {
    const dateValue = (event.value as Date | null) ?? null;
    this.control.setValue(this.formatDateToISO(dateValue));
  }

  onRangeInputChange(event: any): void {
    const startDate = event.value?.start ?? null;
    const endDate = event.value?.end ?? null;

    this.control.setValue({
      start: this.formatDateToISO(startDate),
      end: this.formatDateToISO(endDate),
    });
    this.control.updateValueAndValidity({emitEvent: true});
  }

  onBlur(): void {
    this.control.markAsTouched();
  }

  clear(): void {
    const newValue = this.mode === 'single' ? null : {start: null, end: null};
    this.control.setValue(newValue);
  }
}
